/**
 * VerificationPage — public /verification (iter 142)
 *
 * Short explainer page linked from the homepage <VerificationStrip />
 * icons. Explains the trust mechanic (phone + work-auth + address) in
 * plain English so curious visitors can read it before deciding to
 * sign up.
 *
 * Copy rules (per iter-136 boundaries):
 *   • Never ask or mention citizenship / national origin / visa / immigration status
 *   • No unverified statistics / benchmarks / customer names
 *   • No pricing
 *   • US-wide positioning; no LA-only claims
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ShieldCheck, MapPin, Lock, ArrowRight, Info } from "lucide-react";

const STEPS = [
    {
        Icon: Phone,
        title: "US mobile number",
        body: "You verify a real US mobile via a 6-digit SMS code. We store only the phone number, never full call logs.",
    },
    {
        Icon: ShieldCheck,
        title: "US work authorization",
        body: "You confirm — with a single checkbox — that you're authorized to work in the United States. We never ask about your citizenship, national origin, visa, or immigration status. All we store is a boolean plus a timestamp.",
    },
    {
        Icon: MapPin,
        title: "State + ZIP on file",
        body: "Talent self-reports their state and ZIP at signup. Simonara does not verify street addresses.",
    },
];

const VerificationPage = () => (
    <div className="min-h-screen bg-background text-foreground" data-testid="verification-page">
        <div className="container-page section-pad">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-3xl mx-auto"
            >
                <p className="text-center overline inline-flex items-center gap-1.5 mx-auto">
                    <ShieldCheck size={11} /> Trust layer
                </p>
                <h1 className="mt-3 font-heading font-semibold text-4xl sm:text-5xl tracking-tight text-center text-foreground leading-[1.1]">
                    How verification works on Simonara
                </h1>
                <p className="mt-4 text-lg text-muted-foreground text-center leading-relaxed">
                    Employers don't see your Talent Passport until you finish three short steps. Together they take about two minutes and keep the platform free of bots, non-US signups, and burner accounts. We never ask about citizenship or immigration status.
                </p>

                <div className="mt-10 space-y-4">
                    {STEPS.map(({ Icon, title, body }, i) => (
                        <div
                            key={title}
                            className="rounded-2xl border border-border bg-card p-5 flex gap-4"
                            data-testid={`verification-step-${i + 1}`}
                        >
                            <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 flex items-center justify-center">
                                <Icon size={18} />
                            </div>
                            <div>
                                <div className="text-xs uppercase text-muted-foreground font-semibold">Step {i + 1}</div>
                                <h3 className="font-semibold text-lg mt-0.5">{title}</h3>
                                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{body}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-10 rounded-2xl border border-border bg-muted/30 p-6">
                    <div className="inline-flex items-center gap-2 text-emerald-700 dark:text-emerald-400 text-xs font-semibold uppercase">
                        <Lock size={12} /> What we deliberately don't collect
                    </div>
                    <ul className="mt-3 text-sm text-foreground space-y-1.5 list-disc list-inside">
                        <li>Citizenship, national origin, visa, or immigration status</li>
                        <li>Full home street address (state and ZIP only)</li>
                        <li>Government-issued ID at signup (identity verification is a separate, opt-in step through a licensed provider). Employers can request government-ID verification on any candidate. The candidate completes a secure ID check; the employer sees only the verified outcome — never the ID document.</li>
                        <li>Payment information (creating a Talent Passport is free)</li>
                    </ul>
                </div>

                <div className="mt-8 rounded-xl border border-border p-4 bg-background flex items-start gap-3">
                    <Info size={16} className="text-muted-foreground shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                        Simonara currently serves US-based employers and professionals. When we expand internationally we'll notify anyone on our waitlist first.
                    </p>
                </div>

                <div className="mt-10 text-center">
                    <Link
                        to="/talent/signup"
                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-6 py-3 transition"
                        data-testid="verification-page-cta"
                    >
                        Start your Talent Passport <ArrowRight size={14} />
                    </Link>
                    <p className="mt-3 text-xs text-muted-foreground">Free to build. No credit card. No spam.</p>
                </div>
            </motion.div>
        </div>
    </div>
);

export default VerificationPage;
